import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { Toast } from 'antd-mobile'
import bankData from '../utils/bankData'
import { Spin } from 'antd'
import api from '../utils/api'
import { useAuth } from '../hooks/AuthContext'

const FormContainer = styled.form`
  max-width: 800px;
  margin: 0 auto;
  padding: 10px;
`

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -10px;
`

const FormColumn = styled.div`
  flex: 1;
  padding: 0 10px;
  min-width: 200px;
`

const FormGroup = styled.div`
  margin-bottom: 20px;
`

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
`

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
`

const Select = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
`

const Button = styled.button`
  background-color: #d5728a;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const SwitchLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 55px;
  height: 25px;
  margin-left: 10px;
`

const SwitchInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #d5728a;
  }

  &:focus + span {
    box-shadow: 0 0 1px #d5728a;
  }

  &:checked + span:before {
    -webkit-transform: translateX(27px);
    -ms-transform: translateX(27px);
    transform: translateX(27px);
  }
`

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 34px;

  &:before {
    position: absolute;
    content: '';
    height: 20px;
    width: 20px;
    left: 4px;
    bottom: 3px;
    background-color: white;
    -webkit-transition: 0.4s;
    transition: 0.4s;
    border-radius: 50%;
  }
`

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`

const SwitchText = styled.span`
  font-size: 18px;
  font-weight: bold;
`

const ErrorMessage = styled.span`
  color: red;
  font-size: 12px;
  margin-top: 5px;
  display: block;
`

const HintText = styled.p`
  font-size: 12px;
  color: #666;
  margin-top: 5px;
`
const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
`
const StyledButton = styled(Button)`
  width: 100%;
  height: 50px;
  font-size: 16px;
`

const StatusText = styled.span`
  color: red;
  font-size: 12px;
  margin-left: 8px;
  font-weight: bold;
`

const depositConditionOptions = [
  { value: 'all', label: '全部收取' },
  { value: 'newCustomer', label: '針對新客收取(尚未來過)' },
  { value: 'visitedOnce', label: '來訪1次，下次預約不收取' },
  { value: 'visitedTwice', label: '來訪2次，下次預約不收取' },
  { value: 'visitedThrice', label: '來訪3次，下次預約不收取' }
]

const DepositSettings = () => {
  const [formData, setFormData] = useState({
    isEnabled: false,
    bankCode: '',
    bankName: '',
    accountNumber: '',
    depositAmount: '',
    autoCancelHours: '',
    description: '',
    depositCondition: 'all',
    enableAutoCancel: true, // 新增：控制是否啟用自動取消
    allowOnlineCardPayment: false, // 客人在前台線上刷卡付定金
    allowCounterCardPayment: false // 店家櫃檯刷卡後回後台標記
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  // 有沒有綁定自己的藍新帳號。沒綁的話「線上刷卡」開了也不會生效，
  // 後端會擋下來 —— 這裡要當場說清楚，不要讓店家對著沒反應的前台猜
  // null = 還沒讀到。物件內含 hasPaymentConfig / hasApplied / applicationStatus，
  // 三者組合起來才說得出店家到底卡在哪一步
  const [paymentStatus, setPaymentStatus] = useState(null)
  const { user } = useAuth()
  const businessId = user.businessId

  const fetchPaymentConfigStatus = useCallback(async () => {
    try {
      const res = await api.get(`/api/business_B/${businessId}/payment-settings`)
      setPaymentStatus(res?.data?.data || null)
    } catch (error) {
      // 讀不到就不下結論，維持 null（不顯示任何警告，寧可少說也不要說錯）
      setPaymentStatus(null)
    }
  }, [businessId])

  useEffect(() => {
    if (businessId) fetchPaymentConfigStatus()
  }, [businessId, fetchPaymentConfigStatus])

  const fetchDepositSettings = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/deposit-settings`
      )
      setFormData((prev) => ({
        ...prev,
        ...response.data.data
      }))
    } catch (error) {
      Toast.show({
        content: '獲取定金設置失敗，請稍後再試',
        position: 'top'
      })
    } finally {
      setIsLoading(false)
    }
  }, [businessId])

  useEffect(() => {
    fetchDepositSettings()
  }, [fetchDepositSettings])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name === 'bankCode') {
      const selectedBank = bankData.find((bank) => bank.code === value)
      setFormData((prevData) => ({
        ...prevData,
        bankCode: selectedBank.code,
        bankName: selectedBank.name
      }))
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: type === 'checkbox' ? checked : value
      }))
    }
    setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }))
  }

  const validateForm = () => {
    const newErrors = {}
    if (formData.isEnabled) {
      if (!formData.bankCode) newErrors.bankCode = '請選擇銀行'
      if (!formData.accountNumber) newErrors.accountNumber = '請輸入帳號'
      if (!formData.depositAmount)
        newErrors.depositAmount = '請輸入最低收款金額'
      else if (Number(formData.depositAmount) < 1)
        newErrors.depositAmount = '最低收款金額不能小於1'
      if (formData.enableAutoCancel) {
        if (!formData.autoCancelHours)
          newErrors.autoCancelHours = '請輸入未付款自動取消時間'
        else if (Number(formData.autoCancelHours) < 1)
          newErrors.autoCancelHours = '未付款自動取消時間不能小於1小時'
      }
      if (!formData.description) newErrors.description = '請輸入定金說明'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (validateForm()) {
      setIsLoading(true)
      try {
        await api.put(
          `/api/business_B/${businessId}/deposit-settings`,
          formData
        )
        Toast.show({
          content: '定金設置已更新',
          position: 'bottom'
        })
      } catch (error) {
        Toast.show({
          content: '更新失敗，請稍後再試',
          position: 'bottom'
        })
      } finally {
        setIsLoading(false)
      }
    }
  }

  if (isLoading) {
    return (
      <Spin
        size='large'
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      />
    )
  }

  return (
    <FormContainer onSubmit={handleSubmit}>
      <SwitchContainer>
        <SwitchText>開啟定金功能</SwitchText>
        <SwitchLabel>
          <SwitchInput
            type='checkbox'
            name='isEnabled'
            checked={formData.isEnabled}
            onChange={handleChange}
          />
          <Slider />
        </SwitchLabel>
      </SwitchContainer>

      <FormRow>
        <FormColumn>
          <FormGroup>
            <Label htmlFor='bankCode'>收款銀行</Label>
            <Select
              id='bankCode'
              name='bankCode'
              value={formData.bankCode}
              onChange={handleChange}
              disabled={!formData.isEnabled}
            >
              <option value=''>請選擇銀行</option>
              {bankData.map((bank) => (
                <option key={bank.code} value={bank.code}>
                  {bank.code} {bank.name}
                </option>
              ))}
            </Select>
            {errors.bankCode && <ErrorMessage>{errors.bankCode}</ErrorMessage>}
          </FormGroup>
        </FormColumn>
        <FormColumn>
          <FormGroup>
            <Label htmlFor='accountNumber'>銀行帳號</Label>
            <Input
              type='text'
              id='accountNumber'
              name='accountNumber'
              value={formData.accountNumber}
              onChange={handleChange}
              disabled={!formData.isEnabled}
            />
            {errors.accountNumber && (
              <ErrorMessage>{errors.accountNumber}</ErrorMessage>
            )}
          </FormGroup>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <FormGroup>
            <Label htmlFor='depositAmount'>收款金額（最低金額）</Label>
            <Input
              type='number'
              id='depositAmount'
              name='depositAmount'
              placeholder='請輸入最低收款金額（最小為1）'
              value={formData.depositAmount}
              onChange={handleChange}
              disabled={!formData.isEnabled}
              min='1'
            />
            {errors.depositAmount && (
              <ErrorMessage>{errors.depositAmount}</ErrorMessage>
            )}
          </FormGroup>
        </FormColumn>
        <FormColumn>
          <FormGroup>
            <Label htmlFor='autoCancelHours'>
              未付款自動取消（小時）
              <StatusText>
                {formData.enableAutoCancel ? '啟用中' : '關閉中'}
              </StatusText>
            </Label>
            <Input
              type='number'
              id='autoCancelHours'
              name='autoCancelHours'
              placeholder='請輸入未付款自動取消時間（最小為1小時）'
              value={formData.autoCancelHours}
              onChange={handleChange}
              disabled={!formData.isEnabled || !formData.enableAutoCancel}
              min='1'
            />
            {errors.autoCancelHours && (
              <ErrorMessage>{errors.autoCancelHours}</ErrorMessage>
            )}
            <HintText>
              開啟後將依據狀態自動取消預約，以是否回報後五碼為準
            </HintText>
          </FormGroup>
        </FormColumn>
      </FormRow>

      {/* 定金收款方式 */}
      <SwitchContainer>
        <SwitchText>
          開放客人線上刷卡付定金
          <HintText style={{ margin: '4px 0 0' }}>
            需先完成「金流設定」綁定自己的藍新帳號，否則客人點下去會失敗。
            刷卡手續費會依費率設定計入該筆結帳的支出。
          </HintText>
        </SwitchText>
        <SwitchLabel>
          <SwitchInput
            type='checkbox'
            name='allowOnlineCardPayment'
            checked={formData.allowOnlineCardPayment}
            onChange={handleChange}
            disabled={!formData.isEnabled}
          />
          <Slider />
        </SwitchLabel>
      </SwitchContainer>

      {formData.isEnabled &&
        formData.allowOnlineCardPayment &&
        paymentStatus &&
        paymentStatus.hasPaymentConfig !== true &&
        (() => {
          // 定金會直接進本店的收單帳戶，所以一定要有本店自己的藍新金鑰。
          // 沒有就不開放 —— 否則客人點了刷卡只會看到錯誤頁。
          const approved = paymentStatus.applicationStatus === 'approved'
          const rejected = paymentStatus.applicationStatus === 'rejected'
          let detail
          if (approved) {
            detail = (
              <>
                金流申請<strong>已審核通過</strong>，但藍新帳號的綁定尚未完成
                —— 審核通過和帳號綁定是兩個步驟，綁定由平台端為你設定。
                請聯繫客服提供藍新的商店代號與金鑰，完成後這個開關會自動生效。
              </>
            )
          } else if (rejected) {
            detail = <>金流申請未通過，請到「金流設定」查看審核意見後重新送出。</>
          } else if (paymentStatus.hasApplied) {
            detail = <>金流申請審核中，通過並完成藍新帳號綁定後就會自動生效。</>
          } else {
            detail = (
              <>本店尚未申請金流，請先到「金流設定」提出申請，通過後再完成綁定。</>
            )
          }
          return (
            <div
              style={{
                margin: '-8px 0 16px',
                padding: '12px 15px',
                borderRadius: 10,
                background: '#fff7e6',
                border: '1px solid #ffd591',
                color: '#ad6800',
                fontSize: 13,
                lineHeight: 1.8
              }}
            >
              這個開關目前<strong>還不會生效</strong>：{detail}
              <br />
              <span style={{ color: '#8c8c8c' }}>
                定金會直接進本店的收單帳戶，所以必須使用本店自己的藍新金鑰。
                （「快速收款」沒綁定也能用，走的是系統預設設定，
                所以能用快速收款不代表已完成綁定。）
                在綁定完成前，前台不會顯示刷卡入口，客人仍可正常匯款。
              </span>
            </div>
          )
        })()}

      <SwitchContainer>
        <SwitchText>
          允許標記「櫃檯刷卡」
          <HintText style={{ margin: '4px 0 0' }}>
            用自己的實體刷卡機收定金後，在預約詳情把收款方式標成刷卡。
            不需串接金流，但手續費一樣會計入報表。
          </HintText>
        </SwitchText>
        <SwitchLabel>
          <SwitchInput
            type='checkbox'
            name='allowCounterCardPayment'
            checked={formData.allowCounterCardPayment}
            onChange={handleChange}
            disabled={!formData.isEnabled}
          />
          <Slider />
        </SwitchLabel>
      </SwitchContainer>

      {/* 新增：是否啟用自動取消開關 */}
      <SwitchContainer>
        <SwitchText>未付款自動取消功能</SwitchText>
        <SwitchLabel>
          <SwitchInput
            type='checkbox'
            name='enableAutoCancel'
            checked={formData.enableAutoCancel}
            onChange={handleChange}
            disabled={!formData.isEnabled}
          />
          <Slider />
        </SwitchLabel>
      </SwitchContainer>

      <FormRow>
        <FormColumn>
          <FormGroup>
            <Label htmlFor='description'>定金說明</Label>
            <TextArea
              id='description'
              name='description'
              value={formData.description}
              onChange={handleChange}
              rows={4}
              disabled={!formData.isEnabled}
            />
            {errors.description && (
              <ErrorMessage>{errors.description}</ErrorMessage>
            )}
          </FormGroup>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <FormGroup>
            <Label htmlFor='depositCondition'>收費情境</Label>
            <Select
              id='depositCondition'
              name='depositCondition'
              value={formData.depositCondition}
              onChange={handleChange}
              disabled={!formData.isEnabled}
            >
              {depositConditionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <HintText>
              設定何種情況下需要收取定金，可依據顧客來訪次數調整
            </HintText>
          </FormGroup>
        </FormColumn>
      </FormRow>

      <ButtonGroup>
        <StyledButton type='submit' onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? '保存中...' : '保存'}
        </StyledButton>
      </ButtonGroup>
    </FormContainer>
  )
}

export default DepositSettings
